import { Box, Button, List, TextField, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

import { monospaceStyle } from "../../theme";
import FilterRow from "./FilterRow";

// One value of a counted list
export interface CountedFilterValue {
  // What picking the row picks
  value: string;
  label: string;
  // The count to show; the list computes none
  count: number;
  // Drawn before the label, e.g. a country's flag
  icon?: React.ReactNode;
}

// Values a list shows before it is cut
export const COUNTED_FILTER_LIMIT = 20;

// Pause (ms) in the typing after which the typed text is reported
export const NARROW_DELAY_MS = 250;

export interface CountedFilterListProps {
  // What the list filters by; names the list and its box
  label: string;
  // The values, in the order to show them. They may be fewer than the list
  // has (see `total`).
  values: CountedFilterValue[];
  // Values the whole list has; absent when `values` are all of them
  total?: number;
  // The values picked
  selected: readonly string[];
  onToggle: (value: string, selected: boolean) => void;
  // Values shown before the cut
  limit?: number;
  // Called when the rest is asked for, so that the owner can fetch the
  // values it did not give
  onShowAll?: () => void;
  // The text of the box that shortens the list, held by the owner
  narrowText?: string;
  // Called with the typed text once the typing pauses, so that the owner
  // can fetch the shortened list. The list has a box only when this is given.
  onNarrowChange?: (text: string) => void;
}

// A filter as a flat list: values with a checkbox, a label and a count, cut
// at a limit with "N of M" and a way to show the rest, and an optional box
// that asks the owner for a shortened list. A picked value is shown beyond
// the cut too, so that it can be removed.
const CountedFilterList: React.FC<CountedFilterListProps> = ({
  label,
  values,
  total,
  selected,
  onToggle,
  limit = COUNTED_FILTER_LIMIT,
  onShowAll,
  narrowText = "",
  onNarrowChange,
}) => {
  const [showAll, setShowAll] = useState(false);
  const [draft, setDraft] = useState(narrowText);

  // The text last reported to the owner. The owner hands it back as
  // `narrowText`; only another text is a change from outside.
  const reported = useRef(narrowText);
  const report = useRef(onNarrowChange);
  report.current = onNarrowChange;

  useEffect(() => {
    if (narrowText !== reported.current) {
      reported.current = narrowText;
      setDraft(narrowText);
    }
  }, [narrowText]);

  useEffect(() => {
    if (draft === reported.current) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      reported.current = draft;
      report.current?.(draft);
    }, NARROW_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [draft]);

  const visible = showAll
    ? values
    : values.filter(
        (value, index) => index < limit || selected.includes(value.value),
      );
  const whole = Math.max(total ?? values.length, values.length);

  return (
    <Box>
      {onNarrowChange && (
        <TextField
          fullWidth
          size="small"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Type to shorten"
          inputProps={{ "aria-label": `Find in ${label}`, maxLength: 100 }}
          sx={{
            mb: 0.5,
            "& .MuiOutlinedInput-input": { fontSize: "0.8125rem", py: 0.5 },
          }}
        />
      )}

      <List disablePadding aria-label={label}>
        {visible.map((value) => (
          <FilterRow
            key={value.value}
            label={value.label}
            icon={value.icon}
            count={value.count}
            checked={selected.includes(value.value)}
            onChange={(checked) => onToggle(value.value, checked)}
          />
        ))}
      </List>

      {visible.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          {draft.trim() ? "No match" : "No values"}
        </Typography>
      )}

      {visible.length > 0 && visible.length < whole && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: 28,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={monospaceStyle}
          >
            {`${visible.length} of ${whole}`}
          </Typography>
          {!showAll && (
            <Button
              variant="text"
              size="small"
              onClick={() => {
                setShowAll(true);
                onShowAll?.();
              }}
              sx={{ textTransform: "none", minWidth: 0, py: 0 }}
            >
              {`Show all ${whole}`}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CountedFilterList;
